import React, { useRef, useEffect, useState, useImperativeHandle, forwardRef } from 'react';

import styles from './RestrictedVideoPlayer.module.css';

export interface RestrictedVideoPlayerProps {
    src: string;
    onEnded?: () => void;
    onTimeUpdate?: (data: { currentTime: number; duration: number; percentWatched: number }) => void;
    onLoadedMetadata?: (data: { duration: number }) => void;
    onPlay?: () => void;
    onPause?: () => void;
    initialTime?: number;
    _duration?: number;
    allowSeeking?: boolean;
    autoPlay?: boolean;
}

const RestrictedVideoPlayer = forwardRef<HTMLVideoElement, RestrictedVideoPlayerProps>(({
    src,
    onEnded,
    onTimeUpdate,
    onLoadedMetadata,
    onPlay,
    onPause,
    initialTime = 0,
    _duration,
    allowSeeking = false,
    autoPlay = false
}, ref) => {
    const videoRef = useRef<HTMLVideoElement>(null);
    const progressRef = useRef<HTMLDivElement>(null);
    const lastValidTimeRef = useRef(initialTime);

    // Synchronize lastValidTimeRef with prop changes (critical for correct resume)
    useEffect(() => {
        lastValidTimeRef.current = initialTime;
    }, [src, initialTime]);

    useImperativeHandle(ref, () => videoRef.current!);

    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [videoDuration, setVideoDuration] = useState(0);
    const [error, setError] = useState<string | null>(null);
    const [isBuffering, setIsBuffering] = useState(true); // Start as true to show spinner immediately
    const [networkError, setNetworkError] = useState<string | null>(null);

    const handlePlayPause = () => {
        if (videoRef.current) {
            if (isPlaying) {
                videoRef.current.pause();
                setIsPlaying(false);
            } else {
                const playPromise = videoRef.current.play();
                if (playPromise !== undefined) {
                    playPromise.catch(error => {
                        if (error.name !== 'AbortError') {
                            console.error('Play error:', error);
                            setIsPlaying(false);
                        }
                    });
                }
                setIsPlaying(true);
            }
        }
    };

    const handleRetry = () => {
        setError(null);
        setNetworkError(null);
        setIsBuffering(true);
        if (videoRef.current) {
            videoRef.current.load();
            videoRef.current.play().catch(err => {
                if (err.name !== 'AbortError') {
                    console.error('Retry play error:', err);
                }
            });
        }
    };

    const handleTimeUpdate = () => {
        if (videoRef.current) {
            setCurrentTime(videoRef.current.currentTime);

            if (typeof window !== 'undefined') {
                window.dispatchEvent(new CustomEvent('fastrack:activity'));
            }

            onTimeUpdate?.({
                currentTime: videoRef.current.currentTime,
                duration: videoRef.current.duration,
                percentWatched: (videoRef.current.currentTime / videoRef.current.duration) * 100
            });
        }
    };

    const handleLoadedMetadata = () => {
        if (videoRef.current) {
            console.log('[VideoPlayer] Metadata Loaded', { duration: videoRef.current.duration, initialTime });
            setVideoDuration(videoRef.current.duration);

            // Apply initial time from progress if provided
            if (initialTime > 0 && initialTime < videoRef.current.duration) {
                videoRef.current.currentTime = initialTime;
                lastValidTimeRef.current = initialTime;
            }

            onLoadedMetadata?.({
                duration: videoRef.current.duration
            });

            if (autoPlay) {
                videoRef.current.play().catch(err => {
                    if (err.name !== 'AbortError') {
                        console.error('[VideoPlayer] AutoPlay failed:', err);
                    }
                });
            }
        }
    };

    const handleProgressBarClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (!allowSeeking || !videoRef.current || !progressRef.current || videoDuration === 0) return;

        const rect = progressRef.current.getBoundingClientRect();
        const clickX = e.clientX - rect.left;
        const width = rect.width;
        const newTime = (clickX / width) * videoDuration;

        videoRef.current.currentTime = newTime;
    };

    const handleVideoEnded = () => {
        setIsPlaying(false);
        onEnded?.();
    };

    const formatTime = (seconds: number) => {
        if (!seconds || isNaN(seconds)) return '0:00';
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const progressPercent = videoDuration > 0 ? (currentTime / videoDuration) * 100 : 0;

    useEffect(() => {
        const videoElement = videoRef.current;
        if (!videoElement) return;

        const handlePlay = () => {
            console.log('[VideoPlayer] Play event', { src });
            setIsPlaying(true);
            setNetworkError(null);
            onPlay?.();
        };
        const handlePause = () => {
            console.log('[VideoPlayer] Pause event');
            setIsPlaying(false);
            onPause?.();
        };
        const handleError = (e: Event) => {
            console.error('[VideoPlayer] Error event:', videoElement.error, e);
            const networkMsg = 'Network error: Unable to load video. Check your connection and try again.';
            setNetworkError(networkMsg);
            setError(networkMsg);
            setIsBuffering(false);
        };
        const handleWaiting = () => {
            console.log('[VideoPlayer] Waiting (Buffering)...');
            setIsBuffering(true);
        }
        const handleCanPlay = () => {
            console.log('[VideoPlayer] CanPlay event', { readyState: videoElement.readyState });
            setIsBuffering(false);
        }
        const handleLoadedData = () => {
            console.log('[VideoPlayer] LoadedData event', { readyState: videoElement.readyState });
            setIsBuffering(false);
            setNetworkError(null);
        };
        const handlePlaying = () => {
            console.log('[VideoPlayer] Playing event');
            setIsBuffering(false);
        };
        const handleStalled = () => {
            console.log('[VideoPlayer] Stalled event');
            // Stalled doesn't always mean buffering, but if we are playing it might
        };
        const handleSuspend = () => {
            console.log('[VideoPlayer] Suspend event');
            // Suspend can happen when enough data is loaded
            if (videoElement.readyState >= 3) {
                setIsBuffering(false);
            }
        };
        const handleProgress = () => {
            // If we have data, we might not be buffering anymore
            if (videoElement.readyState >= 3) {
                setIsBuffering(false);
            }
        };
        const handleLoadStart = () => {
            console.log('[VideoPlayer] LoadStart event', { src });
            setIsBuffering(true);
        };

        videoElement.addEventListener('play', handlePlay);
        videoElement.addEventListener('pause', handlePause);
        videoElement.addEventListener('error', handleError);
        videoElement.addEventListener('waiting', handleWaiting);
        videoElement.addEventListener('canplay', handleCanPlay);
        videoElement.addEventListener('canplaythrough', handleCanPlay);
        videoElement.addEventListener('loadeddata', handleLoadedData);
        videoElement.addEventListener('playing', handlePlaying);
        videoElement.addEventListener('stalled', handleStalled);
        videoElement.addEventListener('suspend', handleSuspend);
        videoElement.addEventListener('loadstart', handleLoadStart);
        videoElement.addEventListener('progress', handleProgress);

        return () => {
            videoElement.removeEventListener('play', handlePlay);
            videoElement.removeEventListener('pause', handlePause);
            videoElement.removeEventListener('error', handleError);
            videoElement.removeEventListener('waiting', handleWaiting);
            videoElement.removeEventListener('canplay', handleCanPlay);
            videoElement.removeEventListener('canplaythrough', handleCanPlay);
            videoElement.removeEventListener('loadeddata', handleLoadedData);
            videoElement.removeEventListener('playing', handlePlaying);
            videoElement.removeEventListener('stalled', handleStalled);
            videoElement.removeEventListener('suspend', handleSuspend);
            videoElement.removeEventListener('loadstart', handleLoadStart);
            videoElement.removeEventListener('progress', handleProgress);
        };
    }, [onPause, onPlay, src]);

    useEffect(() => {
        const handleContextMenu = (e: MouseEvent) => {
            e.preventDefault();
            return false;
        };

        const videoElement = videoRef.current;
        if (videoElement) {
            videoElement.addEventListener('contextmenu', handleContextMenu);
            return () => {
                videoElement.removeEventListener('contextmenu', handleContextMenu);
            };
        }
    }, []);

    useEffect(() => {
        const videoElement = videoRef.current;
        if (!videoElement) return;

        const handleSeeking = (e: Event) => {
            if (allowSeeking) return; // Skip restriction if seeking is allowed
            // Only allow seeking if it's very close to lastValidTime (browser jitter)
            if (Math.abs(videoElement.currentTime - lastValidTimeRef.current) > 1.0) {
                e.preventDefault();
                videoElement.currentTime = lastValidTimeRef.current;
            }
        };

        const handleKeyDown = (e: KeyboardEvent) => {
            if (allowSeeking) {
                // Allow arrow keys for seeking if enabled
                if (e.key === 'ArrowRight') {
                    videoElement.currentTime = Math.min(videoElement.duration, videoElement.currentTime + 5);
                    return;
                }
                if (e.key === 'ArrowLeft') {
                    videoElement.currentTime = Math.max(0, videoElement.currentTime - 5);
                    return;
                }
                return;
            }
            const seekKeys = ['ArrowLeft', 'ArrowRight', 'j', 'f', 'l'];
            if (seekKeys.includes(e.key)) {
                e.preventDefault();
                return false;
            }
        };

        const handleTimeUpdateSync = (_e: Event) => {
            // We update lastValidTime as long as they are playing or looking at the video
            // The key is that they can't manually skip ahead via the seek bar
            if (videoElement.currentTime > lastValidTimeRef.current) {
                lastValidTimeRef.current = videoElement.currentTime;
            }
        };

        videoElement.addEventListener('seeking', handleSeeking);
        videoElement.addEventListener('timeupdate', handleTimeUpdateSync);
        videoElement.addEventListener('keydown', handleKeyDown);
        document.addEventListener('keydown', handleKeyDown);

        return () => {
            videoElement.removeEventListener('seeking', handleSeeking);
            videoElement.removeEventListener('timeupdate', handleTimeUpdateSync);
            videoElement.removeEventListener('keydown', handleKeyDown);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, []);

    return (
        <div className={styles.container}>
            {error && (
                <div className={styles.error}>
                    <div className={styles.errorContent}>
                        <div className={styles.errorIcon}>⚠️</div>
                        <div className={styles.errorMessage}>{error}</div>
                        {networkError && (
                            <button
                                className={styles.retryButton}
                                onClick={handleRetry}
                                aria-label="Retry loading video"
                            >
                                🔄 Retry
                            </button>
                        )}
                    </div>
                </div>
            )}

            <div className={styles.playerContent} style={{ display: error ? 'none' : 'block' }}>
                <div className={styles.videoWrapper}>
                    {isBuffering && (
                        <div className={styles.bufferingOverlay}>
                            <div className={styles.spinner} />
                            <div className={styles.bufferingText}>Buffering...</div>
                        </div>
                    )}
                    <video
                        ref={videoRef}
                        src={src}
                        onTimeUpdate={handleTimeUpdate}
                        onLoadedMetadata={handleLoadedMetadata}
                        onEnded={handleVideoEnded}
                        className={styles.video}
                        controlsList="nodownload nofullscreen"
                        preload="auto"
                        playsInline
                        controls={false}
                    />
                </div>

                <div className={styles.customControls}>
                    <div
                        ref={progressRef}
                        className={`${styles.progressContainer} ${allowSeeking ? styles.seekableContainer : ''}`}
                        onClick={handleProgressBarClick}
                    >
                        <div
                            className={`${styles.progressBar} ${allowSeeking ? styles.seekable : ''}`}
                            style={{ width: `${progressPercent}%` }}
                        />
                    </div>

                    <div className={styles.controlsBottom}>
                        <button
                            className={styles.playButton}
                            onClick={handlePlayPause}
                            aria-label={isPlaying ? 'Pause' : 'Play'}
                            disabled={isBuffering && videoDuration === 0}
                        >
                            {isPlaying ? '⏸' : '▶'}
                        </button>

                        <div className={styles.timeDisplay}>
                            <span>{formatTime(currentTime)}</span>
                            <span> / </span>
                            <span>{formatTime(videoDuration)}</span>
                        </div>

                        <div className={styles.seekWarning}>
                            {isBuffering ? '⏳ Buffering...' : allowSeeking ? '✅ Seeking enabled' : '⚠️ Seeking disabled (compliance requirement)'}
                        </div>
                    </div>
                </div>

                <div className={styles.note}>
                    {allowSeeking
                        ? "Note: You have completed this video. Seeking is now available."
                        : "Note: You must watch the entire video to proceed. Seeking is disabled to ensure content compliance."}
                </div>
            </div>
        </div>
    );
});

RestrictedVideoPlayer.displayName = 'RestrictedVideoPlayer';

export default RestrictedVideoPlayer;
